import { ExternalLink, LogOut } from 'lucide-react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useAuth0 } from '@auth0/auth0-react'
import { useTicketCounts } from '@/hooks/useTicketCounts'
import { useTrackerEnabled } from '@/queries/tracker'
import { AvatarInitials } from '@/components/ui/avatar-initials'
import { EtainablLogo } from '@/components/etainabl-logo'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

type ActiveMatcher = 'exact' | ((path: string) => boolean)

// Variant A navbar link (mockup `.nav-a a`): pill highlight in a translucent
// white, never a token colour — the navbar's own dark chrome sits outside
// the light-surface token vocabulary tokens.css defines, so its on-navy
// text/highlight tints are opacity steps on white/black rather than new hex.
function NavLink({
  to,
  label,
  match,
  search,
}: {
  to: string
  label: string
  match: ActiveMatcher
  search?: Record<string, unknown>
}) {
  const path = useRouterState({ select: s => s.location.pathname })
  const active = typeof match === 'function' ? match(path) : path === to

  return (
    <Link
      to={to as never}
      {...(search ? { search: search as never } : {})}
      className={cn(
        'rounded-etn-pill px-3.5 py-1.5 text-etn-control font-medium transition-colors',
        active ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white',
      )}
    >
      {label}
    </Link>
  )
}

function UserMenu() {
  const { user, logout } = useAuth0()
  const name = (user?.name as string | undefined) ?? user?.email ?? 'You'
  const v2Url = import.meta.env.VITE_ETAINABL_UI_URL ?? 'http://localhost:8081'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-etn-pill px-1.5 py-1 text-white/85 hover:text-white">
        <AvatarInitials name={name} size="sm" className="bg-white/15 text-white" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem asChild>
          <a href={v2Url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm">
            <ExternalLink className="h-4 w-4" />
            Open Etainabl
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="flex items-center gap-2 text-sm"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Top navbar, replacing the old left Sidebar (Variant A on V2 — navy bar,
// pill nav, centered content below). Every gate the sidebar enforced moves
// here unchanged:
//  - Tickets: hidden for a non-lensed admin (they work the queue via Admin
//    instead) — same `!isAdmin || lensActive` condition.
//  - Client Tracker: gated on the server-authoritative `useTrackerEnabled`
//    probe, threaded through the *lensed* asUser so a staffer viewing a
//    flagless company's user sees that company's own 404, not their own
//    (bypassed) admin probe.
//  - Admin: unchanged `isAdmin` gate. It was four separate sidebar links
//    (Queue/Manage articles/View as user/Settings) under one "Admin"
//    section; the navbar collapses that into a single link to the queue,
//    with the other three reachable via AdminSubNav on each admin landing
//    page (nothing dropped, just relocated — see AdminSubNav.tsx).
// "New ticket" isn't reproduced here: TicketListView already renders its own
// primary "New ticket" button in the content area (matching the mockup,
// which has no persistent nav item for it either), so nothing is lost.
export function Navbar() {
  const { isAdmin } = useTicketCounts()
  const asUser = useRouterState({ select: s => (s.location.search as { asUser?: string })?.asUser })
  const lensActive = Boolean(asUser)
  const trackerEnabled = useTrackerEnabled(asUser)

  return (
    <header className="flex h-14 shrink-0 items-center gap-1 bg-etn-navbar px-7 text-white">
      <Link to="/" className="mr-6 flex items-center gap-2.5">
        <EtainablLogo variant="mono" className="h-3.5 text-white" />
        <span className="text-etn-micro font-normal tracking-wide text-white/55">Help desk</span>
      </Link>

      {(!isAdmin || lensActive) && (
        <NavLink
          to="/tickets"
          label="Tickets"
          search={{ asUser }}
          match={path => path === '/tickets' || path.startsWith('/tickets/')}
        />
      )}

      <NavLink
        to="/kb"
        label="Knowledge base"
        match={path => path === '/kb' || (path.startsWith('/kb/') && !path.startsWith('/kb/admin'))}
      />

      {trackerEnabled.data === true && (
        <NavLink
          to="/tracker"
          label="Client Tracker"
          search={{ asUser }}
          match={path => path === '/tracker' || path.startsWith('/tracker/')}
        />
      )}

      {isAdmin && (
        <NavLink to="/admin" label="Admin" match={path => path === '/admin' || path.startsWith('/admin/')} />
      )}

      <div className="ml-auto flex items-center">
        <UserMenu />
      </div>
    </header>
  )
}
